import { Stack } from '@mui/material';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';
import { useGetConversationQuery } from 'generated-graphql/hooks/apollo-hooks';
import { Conversation } from 'generated-graphql/types';
import { useEffect } from 'react';
import { useSubscriber } from 'services/api/hooks';
import { onUpdateConversation } from 'services/conversations/subscriptions';
import { useLoadingOverlay } from 'services/loading/hooks';
import { sanitizeObject } from 'utils/fomatting';
import {
  ConversationDetailsHeader,
  ConversationDetailsHeaderProps
} from './ConversationDetailsHeader';
import { ConversationItem } from '../types/ConversationItem';
import { ConversationMessagingDetails } from './ConversationMessagingDetails';
import { useAmplifyPublishEventInterval } from 'core/services/amplify/events/useAmplifyPublishEventInterval';

type Props = {
  conversationId: string;
  shouldHideApplicationStage?: boolean;
  shouldHideCandidateDetails?: boolean;
  shouldHideScreeningInProgress?: boolean;
  headerItems?: Omit<ConversationDetailsHeaderProps, 'conversation'>;
};

export const ConversationDetails: React.FC<Props> = ({
  conversationId,
  shouldHideApplicationStage = false,
  shouldHideCandidateDetails = false,
  shouldHideScreeningInProgress = false,
  headerItems
}) => {
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const {
    data,
    loading: isConversationFetchedLoading,
    updateQuery
  } = useGetConversationQuery({
    variables: {
      id: conversationId
    }
  });

  const conversation = extractQueryGetItem(data);

  useAmplifyPublishEventInterval(
    conversation?.campaignId
      ? {
          channelType: 'CampaignViewConversation',
          channelParams: {
            campaignId: conversation.campaignId
          },
          payload: {
            conversationId: conversation.id
          }
        }
      : null
  );

  const updateConversationLocally = (conversation: ConversationItem) => {
    updateQuery((prev) => {
      if (!prev.getConversation) return prev;
      return {
        getConversation: {
          ...prev.getConversation,
          ...conversation
        }
      };
    });
  };

  useSubscriber({
    subQuery: onUpdateConversation,
    subParams: {
      id: { eq: conversationId || '' }
    },
    onFetchSuccess: (data) => {
      const updatedConversation: Conversation = data?.onUpdateConversation;
      if (!updatedConversation) return;
      updateConversationLocally(
        sanitizeObject<Conversation>(updatedConversation)
      );
    }
  });

  useEffect(() => {
    if (isConversationFetchedLoading) {
      showLoadingOverlay();
    } else {
      hideLoadingOverlay();
    }
  }, [hideLoadingOverlay, isConversationFetchedLoading, showLoadingOverlay]);

  if (!conversationId || !conversation) return null;

  return (
    <Stack sx={{ height: '100%', overflowY: 'auto' }}>
      {headerItems && (
        <ConversationDetailsHeader
          {...headerItems}
          conversation={conversation}
        />
      )}
      <ConversationMessagingDetails
        conversation={conversation}
        shouldHideApplicationStage={shouldHideApplicationStage}
        shouldHideCandidateDetails={shouldHideCandidateDetails}
        shouldHideScreeningInProgress={shouldHideScreeningInProgress}
        onUpdateConversationLocally={updateConversationLocally}
      />
    </Stack>
  );
};
